import React, { useState, useEffect } from "react";
import {
  FaMountain,
  FaCoffee,
  FaUtensils,
  FaStore,
  FaEye,
  FaExternalLinkAlt,
  FaImage,
  FaPlay,
} from "react-icons/fa";

const projects = [
  {
    name: "Top of the World Adventure Pvt. Ltd.",
    desc: "Leading trekking & travel agency in Nepal specializing in Himalayan expeditions and cultural tours",
    type: "Adventure Tourism",
    year: "2015-Present",
    icon: FaMountain,
    color: "from-blue-600 to-cyan-600",
    images: [
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=400&fit=crop",
    ],
    features: [
      "Everest Base Camp Treks",
      "Annapurna Circuit",
      "Cultural Tours",
      "Mountain Expeditions",
    ],
    stats: { clients: "5000+", years: "9", rating: "4.9" },
  },
  {
    name: "Himalayan Java Coffee (TIA Airport)",
    desc: "Premium coffee experience at Tribhuvan International Airport serving travelers from around the world",
    type: "Food & Beverage",
    year: "2018-Present",
    icon: FaCoffee,
    color: "from-amber-600 to-orange-600",
    images: [
      "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1559496417-e7f25cb247cd?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1521017432531-fbd92d768814?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&h=400&fit=crop",
    ],
    features: [
      "Premium Coffee Blends",
      "Airport Location",
      "International Service",
      "Local Sourcing",
    ],
    stats: { customers: "2000+", locations: "1", satisfaction: "95%" },
  },
  {
    name: "Burger House & Crunchy Fried Chicken",
    desc: "Popular food outlet at airport serving quality fast food with a focus on taste and hygiene",
    type: "Food & Beverage",
    year: "2019-Present",
    icon: FaUtensils,
    color: "from-red-600 to-pink-600",
    images: [
      "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1606131731446-5568d87113aa?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1594212699903-ec8a3eca50f5?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=600&h=400&fit=crop",
    ],
    features: [
      "Quality Burgers",
      "Crispy Fried Chicken",
      "Quick Service",
      "Airport Dining",
    ],
    stats: { orders: "1500+", rating: "4.7", speed: "5min" },
  },
  {
    name: "Saan Enterprises Pvt. Ltd.",
    desc: "Comprehensive coffee shop & gift store at airport departure offering souvenirs and refreshments",
    type: "Retail & Hospitality",
    year: "2020-Present",
    icon: FaStore,
    color: "from-green-600 to-emerald-600",
    images: [
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop",
      "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=600&h=400&fit=crop",
    ],
    features: [
      "Departure Lounge Location",
      "Nepalese Souvenirs",
      "Coffee & Snacks",
      "Duty-Free Shopping",
    ],
    stats: { products: "500+", visitors: "3000+", revenue: "Growing" },
  },
];

const projectStats = [
  {
    icon: FaMountain,
    number: "4",
    label: "Active Projects",
    description: "Diverse business portfolio",
  },
  {
    icon: FaStore,
    number: "15+",
    label: "Years",
    description: "Combined experience",
  },
  {
    icon: FaEye,
    number: "10K+",
    label: "Customers",
    description: "Served annually",
  },
  {
    icon: FaExternalLinkAlt,
    number: "100%",
    label: "Success",
    description: "Project completion rate",
  },
];

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [visibleItems, setVisibleItems] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...prev, entry.target.dataset.index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".project-item").forEach((item, index) => {
      item.dataset.index = index;
      observer.observe(item);
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  const openGallery = (projectIndex, imageIndex = 0) => {
    setSelectedProject(projectIndex);
    setSelectedImage(imageIndex);
  };

  const closeGallery = () => {
    setSelectedProject(null);
    setSelectedImage(0);
  };

  const nextImage = () => {
    if (selectedProject !== null) {
      setSelectedImage(
        (prev) => (prev + 1) % projects[selectedProject].images.length
      );
    }
  };

  const prevImage = () => {
    if (selectedProject !== null) {
      setSelectedImage((prev) =>
        prev === 0 ? projects[selectedProject].images.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative pt-24 pb-16 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-6xl md:text-8xl font-black mb-6 bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Projects
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-violet-500 to-purple-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Building diverse business ventures from adventure tourism to
              hospitality, each project reflects passion, innovation, and
              commitment to excellence.
            </p>
          </div>

          {/* Project Stats */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {projectStats.map((stat, index) => (
              <div
                key={index}
                className="group bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-violet-500/50 transition-all duration-500 hover:scale-105 text-center"
              >
                <div className="text-violet-400 mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-8 h-8" />
                </div>
                <div className="text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-lg font-semibold text-violet-300 mb-2">
                  {stat.label}
                </div>
                <div className="text-sm text-gray-400">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid gap-12">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`project-item transition-all duration-1000 ${
                  visibleItems.includes(index.toString())
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-12"
                }`}
              >
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 hover:border-violet-500/50 transition-all duration-500 group">
                  <div className="grid lg:grid-cols-2 gap-0">
                    {/* Image Gallery Section */}
                    <div className="relative overflow-hidden">
                      <div className="aspect-video lg:aspect-square relative">
                        <img
                          src={project.images[0]}
                          alt={project.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                        {/* Image Count Badge */}
                        <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 text-sm font-medium text-white flex items-center space-x-1">
                          <FaImage className="w-3 h-3" />
                          <span>{project.images.length}</span>
                        </div>

                        {/* Gallery Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <button
                            onClick={() => openGallery(index)}
                            className="px-6 py-3 bg-white/20 backdrop-blur-xl rounded-full font-semibold text-white hover:bg-white/30 transition-all duration-300 flex items-center space-x-2"
                          >
                            <FaEye className="w-5 h-5" />
                            <span>View Gallery</span>
                          </button>
                        </div>

                        {/* Project Icon */}
                        <div
                          className={`absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-r ${project.color} rounded-2xl flex items-center justify-center`}
                        >
                          <project.icon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="space-y-6">
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="px-3 py-1 bg-violet-500/20 text-violet-300 rounded-full text-sm font-medium">
                              {project.type}
                            </span>
                            <span className="text-gray-400 text-sm font-medium">
                              {project.year}
                            </span>
                          </div>
                          <h3 className="text-2xl md:text-3xl font-bold text-white group-hover:text-violet-300 transition-colors duration-300 mb-3">
                            {project.name}
                          </h3>
                          <p className="text-gray-300 leading-relaxed">
                            {project.desc}
                          </p>
                        </div>

                        {/* Features */}
                        <div>
                          <h4 className="text-lg font-semibold text-violet-300 mb-3">
                            Key Features:
                          </h4>
                          <div className="grid grid-cols-2 gap-2">
                            {project.features.map((feature, fIndex) => (
                              <div
                                key={fIndex}
                                className="flex items-center space-x-2 text-gray-300"
                              >
                                <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                                <span className="text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-4 pt-4">
                          {Object.entries(project.stats).map(
                            ([key, value], sIndex) => (
                              <div key={sIndex} className="text-center">
                                <div className="text-xl font-bold text-white">
                                  {value}
                                </div>
                                <div className="text-xs text-gray-400 capitalize">
                                  {key}
                                </div>
                              </div>
                            )
                          )}
                        </div>

                        {/* Action Button */}
                        <div className="pt-4">
                          <button
                            onClick={() => openGallery(index)}
                            className={`inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r ${project.color} rounded-full font-semibold text-white hover:shadow-2xl hover:shadow-violet-500/25 transition-all duration-300 hover:scale-105`}
                          >
                            <FaPlay className="w-4 h-4" />
                            <span>Explore Project</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {selectedProject !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4">
          <div className="max-w-6xl w-full max-h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 text-white">
              <h3 className="text-2xl font-bold">
                {projects[selectedProject].name}
              </h3>
              <button
                onClick={closeGallery}
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <span className="text-xl">×</span>
              </button>
            </div>

            {/* Image Display */}
            <div className="relative flex-1 flex items-center justify-center">
              <button
                onClick={prevImage}
                className="absolute left-4 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors text-white"
              >
                ←
              </button>

              <img
                src={projects[selectedProject].images[selectedImage]}
                alt={`${projects[selectedProject].name} ${selectedImage + 1}`}
                className="max-w-full max-h-full object-contain rounded-2xl"
              />

              <button
                onClick={nextImage}
                className="absolute right-4 z-10 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/20 transition-colors text-white"
              >
                →
              </button>
            </div>

            {/* Thumbnail Navigation */}
            <div className="flex justify-center space-x-2 mt-6">
              {projects[selectedProject].images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === selectedImage
                      ? "border-violet-400"
                      : "border-transparent hover:border-white/50"
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-5">
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-violet-500/30 to-purple-500/30 rounded-full blur-3xl"
          style={{
            transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px)`,
            left: "15%",
            top: "25%",
          }}
        ></div>
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-full blur-3xl"
          style={{
            transform: `translate(-${scrollY * 0.1}px, -${scrollY * 0.05}px)`,
            right: "15%",
            bottom: "25%",
          }}
        ></div>
      </div>
    </div>
  );
}
